import React, { useEffect, useMemo, useState } from "react";

/**
 * React port of the VB clsGrid (horizontal, SINGLEROW/NONE selection, serial number "#",
 * client-side header sorting, optional server-driven pagination footer).
 * Suggested location: src/Common/VbGrid.tsx
 */

export interface VbGridColumn {
  header: string;
  align?: "left" | "right" | "center";
  hidden?: boolean;                 // VB: Align "" / "HIDE" -> display:none
  width?: number;                   // VB ColWidths(i) > 0
  type?: "S" | "D" | "N" | "B" | "-";
  html?: boolean;                   // cell value is raw HTML (e.g. "<br />")
}

export interface VbGridPagination {
  startRec: number;
  rowsPerPage: number;
  onNavigate: (startRec: number, rowsPerPage: number) => void;
  showRowsPerPage?: boolean;        // VB ShowRowsPerPage
  showNullsBox?: boolean;           // VB: shown when the hidden ShowNulls field id is supplied
}

interface Props {
  columns: VbGridColumn[];
  rows: string[][];
  title?: string;                   // HTML
  selectable?: boolean;             // SINGLEROW
  selectedIndex?: number | null;    // index into `rows`
  onSelect?: (index: number | null) => void;
  rowTextColors?: string[];         // per row (index into `rows`)
  pagination?: VbGridPagination;
  serialNumber?: boolean;           // default true
  width?: number;
  height?: number;
}

// clsGrid defaults
const MAX_ROWS = 19999;             // m_RowsperPage when no navigation is configured (TPGM 17/08/2026)
const FONT = "Arial";
const FONT_SIZE = "10pt";
const TEXT_HEADER = "#2859ac";
const BACK_HEADER = "#C7DDFD";
const BORDER = "#80A0FF";
const GRID_LINE = "#A3C0E8";
const ROW_BACK = "#F4FAFF";
const ROW_ALT_BACK = "#FFFFFF";
const CELL_PADDING = 8;

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const vbTrim = (s: string) => s.replace(/^ +| +$/g, "");

// VB: CStr(IIf(Trim(x) = "", "&nbsp;", x)), vbCrLf -> <br />, Trim on output
const cellHtml = (v: string | undefined, html?: boolean): string => {
  const t = vbTrim(v ?? "");
  if (t === "") return "&nbsp;";
  return (html ? t : esc(t)).replace(/\r\n/g, "<br />");
};

// dd/MM/yyyy[ HH:mm:ss] -> yyyy/MM/dd[ HH:mm:ss]
const toSortableDate = (v: string): string =>
  v.length >= 10 ? v.slice(6, 10) + v.slice(2, 6) + v.slice(0, 2) + v.slice(10) : v;

const VbGrid: React.FC<Props> = ({
  columns, rows, title, selectable, selectedIndex, onSelect, rowTextColors,
  pagination, serialNumber = true, width, height,
}) => {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<"A" | "D">("A");
  const [rppInput, setRppInput] = useState(String(pagination?.rowsPerPage ?? ""));

  const rpp = pagination ? pagination.rowsPerPage : MAX_ROWS;
  const startRec = pagination ? pagination.startRec : 1;
  const total = rows.length;

  // a fresh page / new data (postback) clears the client-side sort
  useEffect(() => {
    setSortCol(null);
    setSortDir("A");
  }, [rows, startRec, rpp]);

  useEffect(() => {
    if (pagination) setRppInput(String(pagination.rowsPerPage));
  }, [pagination?.rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // VB page maths
  const endRec = Math.min(startRec + rpp - 1, total);
  let totalPages = Math.floor(total / rpp);
  if (total % rpp !== 0) totalPages += 1;
  const curPage = Math.floor(startRec / rpp) + (startRec % rpp !== 0 ? 1 : 0);

  const pageRows = useMemo(() => {
    const out: { orig: number; cells: string[] }[] = [];
    for (let k = startRec; k <= endRec; k++) out.push({ orig: k - 1, cells: rows[k - 1] });
    if (sortCol === null) return out;
    const col = columns[sortCol];
    const type = col?.type ?? "S";
    return [...out].sort((a, b) => {
      let v1 = (a.cells[sortCol] ?? "").toLowerCase().trim();
      let v2 = (b.cells[sortCol] ?? "").toLowerCase().trim();
      if (v1 === "<null>") v1 = "";
      if (v2 === "<null>") v2 = "";
      let cmp: number;
      if (type === "N") {
        cmp = (parseFloat(v1.replace(/,/g, "")) || 0) - (parseFloat(v2.replace(/,/g, "")) || 0);
      } else {
        if (type === "D") { v1 = toSortableDate(v1); v2 = toSortableDate(v2); }
        cmp = v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
      }
      return sortDir === "A" ? cmp : -cmp;
    });
  }, [rows, columns, startRec, endRec, sortCol, sortDir]);

  const handleSort = (i: number) => {
    if (columns[i].hidden) return;
    onSelect?.(null);                                   // VB: fn_Sort calls fn_ClearSelection first
    setSortDir(sortCol === i && sortDir === "A" ? "D" : "A");
    setSortCol(i);
  };

  const handleRowClick = (orig: number) => {
    if (!selectable) return;
    if (selectedIndex === orig) return;                 // VB: CanUnSelect = False -> clicking again keeps it selected
    onSelect?.(orig);
  };

  // ── pagination footer (fn_ConstructPageNavigationString) ───────────────────
  const renderFooter = () => {
    if (!pagination) return null;
    const prevDisabled = curPage <= 1;
    const nextDisabled = curPage >= totalPages;
    const prevPageNo = Math.max(1, curPage - 1);
    const nextPageNo = 0 < totalPages ? curPage + 1 : 0;
    const prevRec = (prevPageNo - 1) * rpp + 1;
    const nextRec = (nextPageNo - 1) * rpp + 1;
    const lastRec = (totalPages - 1) * rpp + 1;

    const parsedRpp = (): number => {
      const n = parseInt(rppInput, 10);
      return Number.isFinite(n) && n >= 1 ? n : rpp;
    };
    const go = (rec: number) => pagination.onNavigate(rec, parsedRpp());

    const link = (label: string, disabled: boolean, rec: number) => (
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); if (!disabled) go(rec); }}
        style={disabled ? { color: "gray", textDecoration: "none" } : { color: "blue" }}
      >
        {label}
      </a>
    );

    return (
      <div style={{
        border: `solid 1px ${BORDER}`, borderTop: `solid 1px ${BORDER}`, color: TEXT_HEADER,
        backgroundColor: BACK_HEADER, fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: "bold",
      }}>
        <table width="100%" border={0} cellPadding={CELL_PADDING} cellSpacing={0}>
          <tbody>
            <tr>
              <td align="center" style={{ backgroundColor: "transparent" }}>
                {pagination.showNullsBox && (
                  <>
                    {/* VB renders this box but fn_ShowErrorLog never uses the value, so it has no effect there either */}
                    <input type="checkbox" />Show Nulls.&nbsp;&nbsp;&nbsp;
                  </>
                )}
                {pagination.showRowsPerPage && (
                  <>
                    Rows per page:{" "}
                    <input
                      type="text"
                      maxLength={3}
                      value={rppInput}
                      onChange={(e) => setRppInput(e.target.value)}
                      style={{ border: "solid 1px #003300", width: 30, textAlign: "center" }}
                    />
                    &nbsp;&nbsp;&nbsp;
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); pagination.onNavigate(startRec, parsedRpp()); }}
                      style={{ color: "blue" }}
                    >
                      Refresh
                    </a>
                  </>
                )}
                &nbsp;&nbsp;&nbsp;&nbsp;{link("<<", prevDisabled, 1)}
                &nbsp;&nbsp;&nbsp;&nbsp;{link("<", prevDisabled, prevRec)}
                &nbsp;&nbsp;&nbsp;&nbsp;{curPage}&nbsp;&nbsp;of&nbsp;&nbsp;{totalPages}
                &nbsp;&nbsp;&nbsp;&nbsp;{link(">", nextDisabled, nextRec)}
                &nbsp;&nbsp;&nbsp;&nbsp;{link(">>", nextDisabled, lastRec)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const cell: React.CSSProperties = {
    fontFamily: FONT, fontSize: FONT_SIZE, padding: CELL_PADDING, verticalAlign: "top", whiteSpace: "nowrap",
  };

  return (
    <div style={{ width, height, display: "flex", flexDirection: "column", fontFamily: FONT, fontSize: FONT_SIZE }}>
      {title && (
        <div style={{
          width: "100%", border: `solid 1px ${BORDER}`, borderBottom: "none", color: TEXT_HEADER,
          backgroundColor: BACK_HEADER, fontFamily: FONT, fontSize: FONT_SIZE, fontWeight: "bold",
        }}>
          <table width="100%" border={0} cellPadding={CELL_PADDING} cellSpacing={0}>
            <tbody>
              <tr><td align="center" dangerouslySetInnerHTML={{ __html: title }} /></tr>
            </tbody>
          </table>
        </div>
      )}

      <div style={{
        flex: "1 1 auto", minHeight: 30, width: "100%", overflow: "scroll", boxSizing: "border-box",
        border: `solid 1px ${BORDER}`,
      }}>
        <table
          cellPadding={0}
          cellSpacing={0}
          style={{ borderCollapse: "collapse", minWidth: "100%", borderBottom: `solid 1px ${BORDER}` }}
        >
          <thead>
            <tr style={{ color: TEXT_HEADER }}>
              {serialNumber && (
                <th style={{
                  ...cell, position: "sticky", top: 0, zIndex: 1, backgroundColor: BACK_HEADER,
                  textAlign: "center", verticalAlign: "bottom", fontWeight: "normal",
                }}>#</th>
              )}
              {columns.map((c, i) => (
                <th
                  key={i}
                  onClick={() => handleSort(i)}
                  style={{
                    ...cell,
                    position: "sticky", top: 0, zIndex: 1, backgroundColor: BACK_HEADER, fontWeight: "normal",
                    verticalAlign: "bottom", textAlign: c.align ?? "left", cursor: "pointer",
                    borderLeft: `solid 1px ${GRID_LINE}`,
                    display: c.hidden ? "none" : undefined,
                  }}
                >
                  {c.header}
                  {sortCol === i && <span style={{ marginLeft: 4, fontSize: 9 }}>{sortDir === "A" ? "▲" : "▼"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, idx) => {
              const isSel = selectable && selectedIndex === r.orig;
              const bg = isSel ? "orange" : idx % 2 === 0 ? ROW_BACK : ROW_ALT_BACK;
              const color = isSel ? "white" : (rowTextColors && rowTextColors[r.orig]) || "black";
              return (
                <tr
                  key={r.orig}
                  onClick={() => handleRowClick(r.orig)}
                  style={{ color, backgroundColor: bg, cursor: selectable ? "pointer" : undefined }}
                >
                  {serialNumber && <td style={{ ...cell, textAlign: "center" }}>{startRec + idx}</td>}
                  {columns.map((c, i) => (
                    <td
                      key={i}
                      style={{
                        ...cell,
                        textAlign: c.align ?? "left",
                        borderLeft: `solid 1px ${GRID_LINE}`,
                        width: c.width && c.width > 0 ? c.width : undefined,
                        display: c.hidden ? "none" : undefined,
                      }}
                      dangerouslySetInnerHTML={{ __html: cellHtml(r.cells[i], c.html) }}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {renderFooter()}
    </div>
  );
};

export default VbGrid;